import React, { useEffect, useRef, useState } from "react";
import { Sparkles, MessageCircle, Globe, Shield, Clock } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const AGENT_CONTAINER_ID = "did-agent-mount";

const featureIcons = [Clock, Globe, Shield, MessageCircle];

const AIAgentSection = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setIsVisible(true),
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Load the D-ID agent script once after the mount target exists
  useEffect(() => {
    if (document.getElementById("did-agent-script")) return;
    const s = document.createElement("script");
    s.id = "did-agent-script";
    s.type = "module";
    s.src = "https://agent.d-id.com/v2/index.js";
    s.setAttribute("data-mode", "full");
    s.setAttribute("data-client-key", "ck_5R-lNpl5YRYkOvNgj0jOz");
    s.setAttribute("data-agent-id", "v2_agt_EHTC6YC_");
    s.setAttribute("data-name", "did-agent");
    s.setAttribute("data-monitor", "true");
    s.setAttribute("data-light-mode", "false");
    s.setAttribute("data-target-id", AGENT_CONTAINER_ID);
    document.body.appendChild(s);
  }, []);

  const features = t("aiAgent.features") || [];

  return (
    <section
      id="ai-agent"
      ref={sectionRef}
      className="relative py-24 overflow-hidden bg-[#0f0f1e]"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#E8792B] blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#2980B9] blur-3xl" />
      </div>
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-center">
          {/* Left: copy + features */}
          <div
            className={`lg:col-span-2 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E8792B]/15 border border-[#E8792B]/30 text-[#E8792B] text-xs font-bold tracking-[0.2em] uppercase mb-5">
              <Sparkles className="w-3.5 h-3.5" />
              {t("aiAgent.eyebrow")}
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5">
              {t("aiAgent.title")}
            </h2>
            <div className="w-16 h-1 bg-[#E8792B] rounded-full mb-6" />
            <p className="text-white/70 text-base lg:text-lg leading-relaxed mb-8">
              {t("aiAgent.description")}
            </p>

            <ul className="space-y-3">
              {Array.isArray(features) &&
                features.map((feat, i) => {
                  const Icon = featureIcons[i % featureIcons.length];
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-[#E8792B]" />
                      </span>
                      <span className="text-white/80 text-sm leading-relaxed pt-2">
                        {feat}
                      </span>
                    </li>
                  );
                })}
            </ul>
          </div>

          {/* Right: agent embed */}
          <div
            className={`lg:col-span-3 transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            {/* Decorative frame */}
            <div className="relative">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#E8792B]/30 to-[#2980B9]/30 blur-2xl scale-95" />
              <div className="relative rounded-3xl border border-white/10 bg-[#1a1a2e] overflow-hidden shadow-2xl">
                {/* Mock toolbar */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 bg-black/20">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
                  </div>
                  <div className="flex items-center gap-2 text-white/40 text-[11px] font-medium tracking-wider uppercase">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                    {t("aiAgent.liveLabel")}
                  </div>
                </div>

                {/* Agent mount point */}
                <div
                  id={AGENT_CONTAINER_ID}
                  data-testid="did-agent-mount"
                  className="w-full bg-[#0f0f1e]"
                  style={{ height: "560px" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIAgentSection;
